import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import React, { createContext, useCallback, useContext, useState } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'wouter';
import { useAppEvent } from '../../../CodesThatsGottaGoSomewhereElse';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { TeamWithMembersData, UserLiteData } from '../../../api/ApiTypes';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Colors, Float, Grid, Menu, remInPx } from '../../../ui';
import { LoadingFrame, LoadingGuard } from '../../../ui/LoadingGuard';
import { DropMenuButton, MenuButton } from '../../../ui/MenuButton';
import { useLocationScope } from '../../AppLocation';
import { AppNavigator } from '../../AppNavigator';
import { useResolve } from '../../Container';
import { useOnTeamAddUserViaPopup, useOnTeamDelete, useOnTeamRename } from '../../actions/WorkspaceActions';
import { DataTable } from '../../common/DataTable';
import { Flexer, LinkButton } from '../../dashboard/elements/LayoutElements';
import { workspaceRemoveMember } from '../../workspace/WorkspaceCommands';
import { ManageFrame } from './ManageFrame';
import { TextCell, UserCell } from './common/TableElements';

const TeamContext = createContext<{ teamId: string } | null>(null);

const ActionsCell = ({ value }: { value: UserLiteData }) => {
    const dispatch = useResolve(CommandDispatcher);
    const { teamId } = useContext(TeamContext)!;
    const [open, setOpen] = useState(false);
    return <Flexer flex="row">
        <DropMenuButton icon="deqo://icons/hero/dots-horizontal" open={open} setOpen={setOpen}>
            <Flexer minWidth="10">
                <Menu>
                    <Menu.Item onClick={() => dispatch(workspaceRemoveMember, [value.id, teamId])}>
                        <Menu.ItemText>
                            Remove User
                        </Menu.ItemText>
                    </Menu.Item>
                </Menu>
            </Flexer>
        </DropMenuButton>
    </Flexer>;
};

const columnHelper = createColumnHelper<UserLiteData>();

const columns = [
    columnHelper.accessor(row => row!, {
        id: 'name',
        size: remInPx(24),
        header: () => <span>Name</span>,
        cell: info => <UserCell value={info.getValue()} />,
    }),
    columnHelper.accessor(row => row.email, {
        id: 'email',
        size: remInPx(18),
        header: () => <span>Email</span>,
        cell: info => <TextCell value={info.getValue()} />
    }),
    columnHelper.accessor(row => row.id, {
        id: 'id',
        size: remInPx(12),
        header: () => <span>User ID</span>,
        cell: info => <TextCell value={info.getValue()} />
    }),
    columnHelper.accessor(row => row, {
        id: 'actions',
        size: remInPx(3),
        header: () => <span></span>,
        cell: info => <ActionsCell value={info.getValue()} />,
        enableSorting: false,
        enableHiding: false,
    }),
    columnHelper.display({
        id: 'null',
        header: () => null,
        cell: () => null,
        enableSorting: false,
        enableHiding: false,
    }),
];

const MemberTableView = ({ data }: { data: TeamWithMembersData }) => {

    const table = useReactTable({
        columns,
        data: data?.members ?? [],
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        enableRowSelection: false,
        initialState: {
            sorting: [
                {
                    id: 'name',
                    desc: true,
                },
            ],
        },
    });

    return <Float shadow="sm">
        <Flexer padding=".5">
            {data && <DataTable table={table} />}
        </Flexer>
    </Float>;
};

const InviteHeader = styled.div`
    padding: .5rem 1rem;
    background: ${Colors.hover};
    color: #546077;
    font-size: .875rem;
    font-weight: 500;
    text-transform: none;
`;

const InviteItem = styled.div`
    padding: .5rem;
    color: #546077;
    text-transform: none;
`;

const InviteView = ({ data }: { data: TeamWithMembersData }) => {

    if (!data?.invites?.length) return null;

    return <Float shadow="sm">
        <Flexer padding=".5">
            <InviteHeader>
                Pending Invites
            </InviteHeader>
            {data.invites.map(x =>
                <InviteItem key={x.id}>
                    {x.email}
                </InviteItem>
            )}
        </Flexer>
    </Float>;
};

const useTeamDetailQuery = (teamId: string) => {

    const api = useResolve(ApiClient);
    const qc = useQueryClient();

    const invalidate = useCallback(() => qc.invalidateQueries(['admin.teams.info']), [qc]);
    useAppEvent('workspace:members:add', invalidate);
    useAppEvent('workspace:members:remove', invalidate);
    useAppEvent('workspace:patch', invalidate);

    return useQuery({
        queryKey: ['admin.teams.info', teamId],
        queryFn: async () => api.exec(v2.admin.teams.info(teamId)),
    });
};

const ManageTeamActionsMenu = ({ team }: { team: TeamWithMembersData }) => {

    const [open, setOpen] = useState(false);
    const setClosed = useCallback(() => setOpen(false), []);

    const { onTeamAddUser } = useOnTeamAddUserViaPopup(team, setClosed);
    const onTeamRename = useOnTeamRename(team.id, setClosed);
    const onTeamDelete = useOnTeamDelete(team.id, setClosed);

    return <MenuButton label="Actions" {...{ open, setOpen }}>
        <Menu>
            <Menu.Section first>
                <Link to={`/${team.id}`}>
                    <Menu.Item>
                        <Menu.ItemText>Open In Dashboard</Menu.ItemText>
                    </Menu.Item>
                </Link>
            </Menu.Section>
            <Menu.Section>
                <Menu.Item onClick={onTeamAddUser}>
                    <Menu.ItemText>Add People</Menu.ItemText>
                </Menu.Item>
                <Menu.Item onClick={onTeamRename}>
                    <Menu.ItemText>Rename Team</Menu.ItemText>
                </Menu.Item>
            </Menu.Section>
            <Menu.Section>
                <Menu.Item onClick={onTeamDelete}>
                    <Menu.ItemText>Delete Team</Menu.ItemText>
                </Menu.Item>
            </Menu.Section>
        </Menu>
    </MenuButton>;
};

export const ManageTeamDetailsView = React.memo(() => {

    const nav = useResolve(AppNavigator);

    const { teamId } = useParams<{ teamId: string }>();
    const { data, isInitialLoading, isLoading } = useTeamDetailQuery(teamId);
    useLocationScope('teams', teamId);

    useAppEvent('workspace:delete', e => {
        if (e.workspaceId === teamId) {
            nav.go('~/manage/teams');
        }
    });

    if (isInitialLoading || !data) return <LoadingFrame />;

    return <TeamContext.Provider value={{ teamId }}>
        <ManageFrame title={`Teams / ${data.label}`}>
            <Flexer maxWidth="82">
                <Flexer padding="1.5 .25" flex="row" align="center" justify="space-between">
                    <LinkButton variant="common" to="~/manage/teams">Back to Teams</LinkButton>
                    <Flexer shrink>
                        <ManageTeamActionsMenu team={data} />
                    </Flexer>
                </Flexer>
                <LoadingGuard loading={isLoading}>
                    <Grid columns={1} rowGap="1">
                        <MemberTableView data={data} />
                        <InviteView data={data} />
                    </Grid>
                </LoadingGuard>
            </Flexer>
        </ManageFrame>
    </TeamContext.Provider>;
});

